import React from 'react';
import { MenuItem } from '../Sidebar';
import { getModuleKeyForPath } from '../../../config/permissionModules';
import { VESSEL_SIDEBAR_MODULES } from '../../../config/vesselPermissionModules';
import { flattenReportCatalogForSearch } from '../../../modules/accounting/reports/reportCatalog';
import { flattenCrewReportCatalogForSearch } from '../../../modules/crew/reportCatalog';
import {
  getModuleGroupKeysForPath,
  isModuleGroupEnabled,
  ModuleEntitlements,
} from '../../../modules/admin/module-management/config';
import {
  normalizeReportSidebarPlacements,
  normalizeSidebarPlacements,
} from '../../../modules/e-forms/sidebarPlacements';

const ListIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

/** Sparkles — AI onboard status report */
const OnboardStatusAiIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
    />
  </svg>
);

const VesselSupplyRequisitionsIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h6m-7 4h8M8 13h8M8 17h5M7 3h10a2 2 0 012 2v14a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" />
  </svg>
);

const VesselNewItemIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const VesselPendingDeliveriesIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
    />
  </svg>
);

const VesselDashboardIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2 7-7 7 7 2 2M5 10v10h14V10" />
  </svg>
);

function vesselModuleListIcon(pathSegment: string): React.ReactNode {
  if (pathSegment === 'dashboard') return VesselDashboardIcon;
  if (pathSegment === 'requisitions') return VesselSupplyRequisitionsIcon;
  if (pathSegment === 'pending-deliveries') return VesselPendingDeliveriesIcon;
  return ListIcon;
}

const VesselModulesGroupIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 13h18l-2 6H5l-2-6zm3-3l3-5h6l3 5H6z" />
  </svg>
);

const SuppliesIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

const AccountingIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m-7 4h8m-8 4h5m-7-9h10a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2z" />
  </svg>
);

const ReportsIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ReferenceIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
  </svg>
);

const SystemIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
  </svg>
);

const sidebarSection = (label: string): MenuItem => ({
  name: label,
  sectionLabel: label,
});

export const sidebarItems: MenuItem[] = [
  sidebarSection('General'),
  {
    name: 'Dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    path: '/dashboard',
    permissionModuleKey: 'dashboard',
    views: [{ type: 'list', label: 'Dashboard', path: '/dashboard', icon: ListIcon }],
  },
  {
    name: 'Notifications',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
    path: '/notifications',
    views: [{ type: 'list', label: 'Notifications', path: '/notifications', icon: ListIcon }],
  },
  {
    name: 'Bookmarks',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    ),
    children: [],
  },
  {
    name: 'Documents',
    moduleGroupKey: 'documents',
    permissionModuleKey: 'documents',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    defaultExpanded: false,
    children: [
      { name: 'Documents', path: '/documents', moduleGroupKey: 'documents', permissionModuleKey: 'documents', icon: ListIcon },
      { name: 'Business rules', path: '/documents/business-rules', moduleGroupKey: 'documents', permissionModuleKey: 'documents', icon: ListIcon },
    ],
  },
  {
    name: 'Approvals',
    moduleGroupKey: 'approvals',
    permissionModuleKey: 'approvals',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h6m-7 4h8m-8 4h5m-7-9h10a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2zM9 16l2 2 4-4" />
      </svg>
    ),
    defaultExpanded: false,
    children: [
      { name: 'Inbox', path: '/approvals', moduleGroupKey: 'approvals', permissionModuleKey: 'approvals', icon: ListIcon },
      { name: 'Delegations', path: '/approvals/delegations', moduleGroupKey: 'approvals', permissionModuleKey: 'approvals', icon: ListIcon },
      { name: 'Templates', path: '/approvals/templates', moduleGroupKey: 'approvals', permissionModuleKey: 'approval_templates', icon: ListIcon },
    ],
  },
  sidebarSection('Work'),
  {
    name: 'E-Forms',
    moduleGroupKey: 'e_forms',
    permissionModuleKey: 'e_form_submissions',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2zm3 4h.01M10 12h.01M10 16h.01" />
      </svg>
    ),
    defaultExpanded: false,
    children: [
      { name: 'Forms', path: '/e-forms', moduleGroupKey: 'e_forms', permissionModuleKey: 'e_form_submissions', icon: ListIcon },
      { name: 'Form Studio', path: '/e-forms/builder', moduleGroupKey: 'e_forms', permissionModuleKey: 'e_form_definitions', icon: ListIcon },
      // Published form names are injected at runtime (see withPublishedEFormsInSidebar).
    ],
  },
  {
    name: 'Operations',
    moduleGroupKey: 'operations',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
        />
      </svg>
    ),
    defaultExpanded: false,
    children: [
      {
        name: 'Noon reports',
        path: '/operations/noon-reports',
        permissionModuleKey: 'noon_reports',
        icon: ListIcon,
        views: [{ type: 'list', label: 'Noon reports', path: '/operations/noon-reports', icon: ListIcon }],
      },
    ],
  },
  {
    name: 'Finance',
    moduleGroupKey: 'accounting',
    icon: AccountingIcon,
    defaultExpanded: false,
    children: [
      { name: 'Journals', path: '/accounting/journals', permissionModuleKey: 'acc_documents', icon: ListIcon },
      { name: 'Invoices', path: '/accounting/invoices/new', permissionModuleKey: 'acc_documents', icon: ListIcon },
      { name: 'Payments', path: '/accounting/payments/new', permissionModuleKey: 'acc_documents', icon: ListIcon },
      { name: 'Analytical ledgers', path: '/accounting/reports/analytical-ledgers', permissionModuleKey: 'accounting', icon: ListIcon },
      { name: 'Chart of accounts', path: '/accounting/coa', permissionModuleKey: 'acc_accounts', icon: ListIcon },
      { name: 'Trial balance', path: '/accounting/reports/trial-balance', permissionModuleKey: 'accounting', icon: ListIcon },
      { name: 'Currency rates', path: '/accounting/currency-rates', permissionModuleKey: 'acc_currency_rates', icon: ListIcon },
      {
        name: 'Reports',
        icon: ReportsIcon,
        path: '/accounting/reports',
        permissionModuleKey: 'accounting',
      },
    ],
  },
  {
    name: 'Human Resources',
    moduleGroupKey: 'hr',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
    defaultExpanded: false,
    children: [
      {
        name: 'Crew',
        moduleGroupKey: 'crew',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        ),
        defaultExpanded: false,
        children: [
          {
            name: 'Crew Pool',
            path: '/crew',
            permissionModuleKey: 'crew',
            icon: ListIcon,
            views: [{ type: 'list', label: 'Crew Pool', path: '/crew', icon: ListIcon }],
          },
          {
            name: 'Crew List',
            path: '/crew/list',
            permissionModuleKey: 'crew',
            icon: ListIcon,
            views: [{ type: 'list', label: 'Crew List', path: '/crew/list', icon: ListIcon }],
          },
          {
            name: 'Onboard Status (AI)',
            path: '/crew/onboard-status',
            permissionModuleKey: 'crew',
            icon: OnboardStatusAiIcon,
            views: [{ type: 'list', label: 'Onboard Status (AI)', path: '/crew/onboard-status', icon: OnboardStatusAiIcon }],
          },
          {
            name: 'Replacement planner',
            path: '/crew/replacement-planner',
            permissionModuleKey: 'crew',
            icon: ListIcon,
            views: [
              {
                type: 'list',
                label: 'Replacement planner',
                path: '/crew/replacement-planner',
                icon: ListIcon,
              },
            ],
          },
          {
            name: 'Crew Changes Cost',
            path: '/crew/crew-changes-cost',
            permissionModuleKey: 'crew',
            icon: ListIcon,
            views: [
              {
                type: 'list',
                label: 'Crew Changes Cost',
                path: '/crew/crew-changes-cost',
                icon: ListIcon,
              },
            ],
          },
          {
            name: 'Relief board',
            path: '/crew/relief-board',
            permissionModuleKey: 'crew',
            icon: ListIcon,
            views: [{ type: 'list', label: 'Relief board', path: '/crew/relief-board', icon: ListIcon }],
          },
          {
            name: 'COE watchlist',
            path: '/crew/coe-watchlist',
            permissionModuleKey: 'crew',
            icon: ListIcon,
            views: [{ type: 'list', label: 'COE watchlist', path: '/crew/coe-watchlist', icon: ListIcon }],
          },
          {
            name: 'Document Gaps',
            path: '/crew/cert-gaps',
            permissionModuleKey: 'crew',
            icon: ListIcon,
            views: [{ type: 'list', label: 'Document Gaps', path: '/crew/cert-gaps', icon: ListIcon }],
          },
          {
            name: 'Statistics',
            path: '/crew/statistics',
            permissionModuleKey: 'crew',
            icon: ReportsIcon,
            views: [{ type: 'list', label: 'Crew statistics', path: '/crew/statistics', icon: ListIcon }],
          },
          {
            name: 'Reports',
            icon: ReportsIcon,
            path: '/crew/reports',
            permissionModuleKey: 'crew',
          },
        ],
      },
      {
        name: 'MGA',
        path: '/mga',
        permissionModuleKey: 'mga',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        ),
        views: [{ type: 'list', label: 'MGA', path: '/mga', icon: ListIcon }],
      },
      {
        name: 'Rest Hours',
        moduleGroupKey: 'wrh',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
        defaultExpanded: false,
        children: [
          {
            name: 'Month sheet',
            path: '/wrh/sheet',
            permissionModuleKey: 'wrh',
            icon: ListIcon,
            views: [{ type: 'list', label: 'Month sheet', path: '/wrh/sheet', icon: ListIcon }],
          },
          {
            name: 'Arrangements',
            path: '/wrh/arrangements',
            permissionModuleKey: 'wrh',
            icon: ListIcon,
            views: [{ type: 'list', label: 'Arrangements', path: '/wrh/arrangements', icon: ListIcon }],
          },
          {
            name: 'Vessel operations',
            path: '/vessel-operations',
            permissionModuleKey: 'wrh',
            icon: ListIcon,
            views: [{ type: 'list', label: 'Vessel operations', path: '/vessel-operations', icon: ListIcon }],
          },
          {
            name: 'Operation types',
            path: '/wrh/operation-types',
            permissionModuleKey: 'wrh',
            icon: ListIcon,
            views: [{ type: 'list', label: 'Operation types', path: '/wrh/operation-types', icon: ListIcon }],
          },
          {
            name: 'Rules',
            path: '/wrh/rules',
            permissionModuleKey: 'wrh',
            icon: ListIcon,
            views: [{ type: 'list', label: 'Rules', path: '/wrh/rules', icon: ListIcon }],
          },
          {
            name: 'Compliance',
            path: '/wrh/compliance',
            permissionModuleKey: 'wrh',
            icon: ListIcon,
            views: [{ type: 'list', label: 'Compliance', path: '/wrh/compliance', icon: ListIcon }],
          },
          {
            name: 'Weekly report',
            path: '/wrh/reports',
            permissionModuleKey: 'wrh',
            icon: ListIcon,
            views: [{ type: 'list', label: 'Weekly report', path: '/wrh/reports', icon: ListIcon }],
          },
        ],
      },
    ],
  },
  {
    name: 'Purchasing',
    moduleGroupKey: 'purchasing',
    icon: SuppliesIcon,
    defaultExpanded: false,
    children: [
      {
        name: 'Port calls',
        path: '/port-calls',
        alsoActiveForPaths: ['/supplies/port-calls', '/spares/port-calls'],
        permissionModuleKeys: ['supply_cases', 'spare_cases'],
        icon: ListIcon,
      },
      {
        name: 'Forwardings',
        path: '/forwardings',
        permissionModuleKeys: ['supply_forwardings', 'spare_forwardings'],
        icon: ListIcon,
      },
      {
        name: 'Supplies',
        moduleGroupKey: 'shipping',
        icon: SuppliesIcon,
        defaultExpanded: false,
        children: [
          { name: 'Cases', path: '/supplies/supply-cases', permissionModuleKey: 'supply_cases', icon: ListIcon },
          { name: 'Requisitions', path: '/supplies/requisitions', permissionModuleKey: 'supply_requisitions', icon: ListIcon },
          { name: 'RFQ', path: '/supplies/supplier-enquiries', permissionModuleKey: 'supplier_enquiries', icon: ListIcon },
          { name: 'Quotations', path: '/supplies/supplier-quotations', permissionModuleKey: 'supplier_quotations', icon: ListIcon },
          { name: 'Evaluations', path: '/supplies/quotation-evaluations', permissionModuleKey: 'quotation_evaluations', icon: ListIcon },
          { name: 'Purchase orders', path: '/supplies/supply-purchase-orders', permissionModuleKey: 'supply_purchase_orders', icon: ListIcon },
          { name: 'Forwardings', path: '/supplies/supply-forwardings', permissionModuleKey: 'supply_forwardings', icon: ListIcon },
          { name: 'Deliveries', path: '/supplies/supply-deliveries', permissionModuleKey: 'supply_deliveries', icon: ListIcon },
          { name: 'Invoices', path: '/supplies/supply-invoices', permissionModuleKey: 'supply_invoices', icon: ListIcon },
        ],
      },
      {
        name: 'Spares',
        moduleGroupKey: 'spares',
        icon: SuppliesIcon,
        defaultExpanded: false,
        children: [
          { name: 'Cases', path: '/spares/spare-cases', permissionModuleKey: 'spare_cases', icon: ListIcon },
          { name: 'Requisitions', path: '/spares/requisitions', permissionModuleKey: 'spare_requisitions', icon: ListIcon },
          { name: 'RFQ', path: '/spares/supplier-enquiries', permissionModuleKey: 'spare_supplier_enquiries', icon: ListIcon },
          { name: 'Quotations', path: '/spares/supplier-quotations', permissionModuleKey: 'spare_supplier_quotations', icon: ListIcon },
          { name: 'Evaluations', path: '/spares/quotation-evaluations', permissionModuleKey: 'spare_quotation_evaluations', icon: ListIcon },
          { name: 'Purchase orders', path: '/spares/spare-purchase-orders', permissionModuleKey: 'spare_purchase_orders', icon: ListIcon },
          { name: 'Forwardings', path: '/spares/spare-forwardings', permissionModuleKey: 'spare_forwardings', icon: ListIcon },
          { name: 'Deliveries', path: '/spares/spare-deliveries', permissionModuleKey: 'spare_deliveries', icon: ListIcon },
          { name: 'Invoices', path: '/spares/spare-invoices', permissionModuleKey: 'spare_invoices', icon: ListIcon },
        ],
      },
    ],
  },
  {
    name: 'PMS',
    moduleGroupKey: 'pms',
    icon: SuppliesIcon,
    defaultExpanded: false,
    children: [
      {
        name: 'Equipment hierarchy',
        path: '/pms/hierarchy',
        permissionModuleKey: 'pms_hierarchy',
        icon: ListIcon,
      },
      {
        name: 'Due jobs',
        path: '/pms/due-jobs',
        permissionModuleKey: 'pms_maintenance_jobs',
        icon: ListIcon,
      },
      { name: 'Counters', path: '/pms/pms-maintenance-counters', permissionModuleKey: 'pms_maintenance_counters', icon: ListIcon },
      { name: 'Work orders', path: '/pms/pms-work-orders', permissionModuleKey: 'pms_work_orders', icon: ListIcon },
      { name: 'Defects', path: '/pms/pms-defects', permissionModuleKey: 'pms_defects', icon: ListIcon },
      { name: 'Tools', path: '/pms/pms-tools', permissionModuleKey: 'pms_tools', icon: ListIcon },
      { name: 'Documents', path: '/pms/pms-documents', permissionModuleKey: 'pms_documents', icon: ListIcon },
    ],
  },
  sidebarSection('Reference Data'),
  {
    name: 'Reference data',
    moduleGroupKey: 'parameters',
    icon: ReferenceIcon,
    defaultExpanded: false,
    children: [
      {
        name: 'General',
        path: '/parameters/general',
        moduleGroupKey: 'core_reference',
        permissionModuleKey: 'vessels',
        icon: ListIcon,
      },
      {
        name: 'Human Resources',
        path: '/parameters/crew',
        moduleGroupKey: 'crew_parameters',
        permissionModuleKey: 'crew_parameters',
        icon: ListIcon,
      },
      {
        name: 'Purchasing',
        path: '/parameters/purchasing',
        moduleGroupKey: 'purchasing',
        permissionModuleKeys: [
          'store_items',
          'store_categories',
          'store_units',
          'supply_forwarding_statuses',
          'spare_items',
          'spare_forwarding_statuses',
        ],
        icon: ListIcon,
      },
      {
        name: 'PMS',
        path: '/parameters/pms',
        moduleGroupKey: 'pms_parameters',
        permissionModuleKey: 'pms_record_statuses',
        icon: ListIcon,
      },
      {
        name: 'Finance',
        path: '/parameters/finance',
        moduleGroupKey: 'accounting',
        permissionModuleKey: 'acc_companies',
        icon: ListIcon,
      },
    ],
  },
  sidebarSection('System'),
  {
    name: 'System',
    moduleGroupKey: 'admin_settings',
    icon: SystemIcon,
    defaultExpanded: false,
    children: [
      {
        name: 'Users & access',
        icon: ListIcon,
        children: [
          { name: 'User management', path: '/admin/users', permissionModuleKey: 'users', icon: ListIcon },
          { name: 'Manning agents', path: '/admin/manning-agents', permissionModuleKey: 'users', icon: ListIcon },
        ],
      },
      {
        name: 'Imports',
        icon: ListIcon,
        children: [
          { name: 'Import IMPA code', path: '/admin/import-actions/impa-code', permissionModuleKey: 'import_impa', icon: ListIcon },
          { name: 'Import accounting (COA)', path: '/admin/import-actions/accounts-plan', permissionModuleKey: 'import_accounts_plan', icon: ListIcon },
          { name: 'Import companies', path: '/admin/import-actions/companies', permissionModuleKey: 'import_companies', icon: ListIcon },
          { name: 'Import journals', path: '/admin/import-actions/journals', permissionModuleKey: 'import_journals', icon: ListIcon },
        ],
      },
      {
        name: 'Templates',
        icon: ListIcon,
        children: [
          { name: 'Excel document templates', path: '/supplies/excel-document-templates', permissionModuleKey: 'supply_requisitions', icon: ListIcon },
          { name: 'Reference templates', path: '/system/reference-templates', permissionModuleKey: 'supply_requisitions', icon: ListIcon },
        ],
      },
      {
        name: 'Tools',
        icon: ListIcon,
        children: [
          { name: 'Application settings', path: '/admin/settings/application', moduleGroupKey: 'admin_settings', permissionModuleKey: 'settings', icon: ListIcon },
          { name: 'Module management', path: '/admin/module-management', moduleGroupKey: 'admin_settings', permissionModuleKey: 'settings', icon: ListIcon },
          { name: 'AI billing', path: '/admin/ai-billing', moduleGroupKey: 'admin_settings', permissionModuleKey: 'ai_billing', icon: ListIcon },
          { name: 'Database browser', path: '/admin/database-browser', moduleGroupKey: 'admin_settings', permissionModuleKey: 'database_browser', icon: ListIcon },
          { name: 'MGA sheet columns', path: '/system/mga-sheet-columns', permissionModuleKey: 'mga', icon: ListIcon },
        ],
      },
    ],
  },
];

/**
 * Menu shape for User Permissions (traverse) — vessel portal module keys (app lives in frontend-vessel).
 */
export function getVesselModulesMenuForPermissions(): MenuItem {
  return {
    name: 'Vessel',
    icon: VesselModulesGroupIcon,
    children: [
      ...VESSEL_SIDEBAR_MODULES.flatMap((mod) => {
        const listPath = `/vessel-portal/${mod.pathSegment}`;
        const out: MenuItem[] = [];
        if (mod.newLink) {
          out.push({
            name: mod.newLink.label,
            path: `${listPath}${mod.newLink.pathSuffix}`,
            permissionModuleKey: mod.permissionModuleKey,
            icon: VesselNewItemIcon,
          });
        }
        out.push({
          name: mod.listLabel,
          path: listPath,
          permissionModuleKey: mod.permissionModuleKey,
          icon: vesselModuleListIcon(mod.pathSegment),
        });
        return out;
      }),
    ],
  };
}

function getItemModuleKey(item: MenuItem): string | undefined {
  if (item.sectionLabel) return undefined;
  if (item.permissionModuleKey) return item.permissionModuleKey;
  const path = item.path ?? item.views?.[0]?.path;
  return path ? getModuleKeyForPath(path) : undefined;
}

function itemAllowedByPermission(item: MenuItem, canView: ((moduleKey: string) => boolean) | undefined): boolean {
  if (item.sectionLabel) return true;
  if (!canView) return true;
  if (item.permissionModuleKeys?.length) {
    return item.permissionModuleKeys.some((key) => canView(key));
  }
  const moduleKey = getItemModuleKey(item);
  if (!moduleKey) return true;
  return canView(moduleKey);
}

function itemAllowedByEntitlement(
  item: MenuItem,
  entitlements: ModuleEntitlements | null | undefined,
): boolean {
  if (item.sectionLabel) return true;
  if (item.name === 'Bookmarks' || item.name === 'Notifications' || item.name === 'Dashboard') return true;
  if (!entitlements) return true;

  if (item.moduleGroupKey) {
    return isModuleGroupEnabled(item.moduleGroupKey, entitlements);
  }

  const path = item.path ?? item.views?.[0]?.path;
  if (path) {
    const keys = getModuleGroupKeysForPath(path);
    if (keys.length === 0) return true;
    return keys.some((k) => isModuleGroupEnabled(k, entitlements));
  }

  // Folder without an explicit pack — keep and let children decide.
  return true;
}

function filterChildrenByAccess(
  children: MenuItem[] | undefined,
  canView: ((moduleKey: string) => boolean) | undefined,
  entitlements: ModuleEntitlements | null | undefined,
): MenuItem[] | undefined {
  if (!children || children.length === 0) return children;
  const filtered = children
    .filter((child) => itemAllowedByEntitlement(child, entitlements) && itemAllowedByPermission(child, canView))
    .map((child) => ({
      ...child,
      children: filterChildrenByAccess(child.children, canView, entitlements),
    }))
    .filter((child) => {
      if (child.sectionLabel) return true;
      const hasContent =
        !!child.path || (child.children && child.children.length > 0) || (child.views && child.views.length > 0);
      return hasContent;
    });
  return filtered.length > 0 ? filtered : undefined;
}

function itemVisibleAfterFilter(item: MenuItem): boolean {
  if (item.sectionLabel) return true;
  if (item.name === 'Bookmarks') return true;
  if (item.name === 'Notifications') return true;
  return !!(item.path || item.children?.length || item.views?.length);
}

/**
 * Full static tree filtered by company entitlements and this user's `canView`.
 * Vessel portal lives in frontend-vessel — not shown in the office sidebar.
 */
export function collectSidebarPathLabels(
  items: MenuItem[],
  trail: string[] = [],
  out: Record<string, string> = {},
): Record<string, string> {
  for (const item of items) {
    if (item.sectionLabel) continue;
    const nextTrail = [...trail, item.name];
    const label = nextTrail.join(' › ');
    if (item.path) out[item.path] = label;
    for (const v of item.views ?? []) {
      if (v.path) out[v.path] = label;
    }
    if (item.children?.length) collectSidebarPathLabels(item.children, nextTrail, out);
  }
  return out;
}

export const getSidebarItems = (
  canView?: (moduleKey: string) => boolean,
  _vesselAccountId?: number | null,
  entitlements?: ModuleEntitlements | null,
  typeLocation?: string | null,
): MenuItem[] => {
  let result = [...sidebarItems];

  if (canView || entitlements) {
    result = result
      .filter((item) => itemAllowedByEntitlement(item, entitlements) && itemAllowedByPermission(item, canView))
      .map((item) => ({
        ...item,
        children: filterChildrenByAccess(item.children, canView, entitlements),
      }))
      .filter(itemVisibleAfterFilter);
  }

  const loc = typeLocation != null ? String(typeLocation).toUpperCase() : '';
  if (loc === 'MANNING_AGENT') {
    // Crew lives under Human Resources → Crew → leaves. Trim to Pool + List only.
    // Keep Bookmarks (and Notifications) so account chrome still works for agency logins.
    const agencyCrewPaths = new Set(['/crew', '/crew/list']);
    const agencyAlwaysKeep = new Set(['Bookmarks', 'Notifications']);
    const filterAgencyMenu = (items: MenuItem[]): MenuItem[] =>
      items
        .map((item) => {
          if (agencyAlwaysKeep.has(item.name)) {
            return item;
          }
          if (item.name === 'Crew' || item.moduleGroupKey === 'crew') {
            const children = (item.children ?? []).filter(
              (leaf) => typeof leaf.path === 'string' && agencyCrewPaths.has(leaf.path),
            );
            return { ...item, children };
          }
          if (item.children?.length) {
            return { ...item, children: filterAgencyMenu(item.children) };
          }
          if (typeof item.path === 'string' && agencyCrewPaths.has(item.path)) {
            return item;
          }
          return null;
        })
        .filter((item): item is MenuItem => item != null && itemVisibleAfterFilter(item));
    result = filterAgencyMenu(result);
  }

  return result;
};

/** Map published e-form definitions to the shape used by `withPublishedEFormsInSidebar`. */
export function toPublishedEFormSidebarInput(
  forms: Array<{
    id: number;
    name: string;
    code?: string;
    isActive?: boolean;
    versions?: Array<{ status: string; schemaJson?: unknown }>;
  }>,
): Array<{ id: number; name: string; code?: string; sidebarPlacements?: unknown }> {
  return forms
    .filter((f) => f.isActive !== false)
    .map((f) => {
      const pub = f.versions?.find((v) => v.status === 'PUBLISHED');
      const schema = pub?.schemaJson as { settings?: { sidebarPlacements?: unknown } } | undefined;
      return {
        id: f.id,
        name: f.name,
        code: f.code,
        sidebarPlacements: schema?.settings?.sidebarPlacements,
      };
    });
}

/** Append each published e-form under the E-Forms group (links to that form's submissions). */
export function withPublishedEFormsInSidebar(
  items: MenuItem[],
  forms: Array<{
    id: number;
    name: string;
    code?: string;
    sidebarPlacements?: unknown;
  }>,
): MenuItem[] {
  if (!forms.length) return items;
  const withCatalog = items.map((item) => {
    if (item.name !== 'E-Forms') return item;
    const existing = item.children ?? [];
    const withoutDynamic = existing.filter(
      (c) =>
        !(
          typeof c.path === 'string' &&
          /^\/e-forms\/\d+\/submissions(\?.*)?$/.test(c.path)
        ),
    );
    const formItems: MenuItem[] = [];
    for (const f of forms) {
      const placements = normalizeSidebarPlacements(f.sidebarPlacements);
      if (!placements.some((p) => p.mode === 'catalog_only')) continue;
      formItems.push({
        name: f.name,
        path: `/e-forms/${f.id}/submissions`,
        moduleGroupKey: 'e_forms',
        permissionModuleKey: 'e_form_submissions',
        icon: ListIcon,
      });
    }
    return { ...item, defaultExpanded: true, children: [...withoutDynamic, ...formItems] };
  });

  type InsertRecord = {
    anchorPath: string;
    insert: 'before' | 'after';
    sortOrder: number;
    item: MenuItem;
  };

  const insertionsBefore = new Map<string, InsertRecord[]>();
  const insertionsAfter = new Map<string, InsertRecord[]>();

  for (const form of forms) {
    const placements = normalizeSidebarPlacements(form.sidebarPlacements);
    const formItem: MenuItem = {
      name: form.name,
      path: `/e-forms/${form.id}/submissions?nav=shortcut`,
      moduleGroupKey: 'e_forms',
      permissionModuleKey: 'e_form_submissions',
      icon: ListIcon,
    };

    for (const p of placements) {
      if (p.mode !== 'shortcut') continue;
      const rec: InsertRecord = {
        anchorPath: p.anchorPath,
        insert: p.insert,
        sortOrder: p.sortOrder ?? 0,
        item: formItem,
      };
      const list = p.insert === 'before'
        ? insertionsBefore.get(p.anchorPath) ?? []
        : insertionsAfter.get(p.anchorPath) ?? [];
      list.push(rec);
      if (p.insert === 'before') insertionsBefore.set(p.anchorPath, list);
      else insertionsAfter.set(p.anchorPath, list);
    }
  }

  const sortInsertions = (list: InsertRecord[]): MenuItem[] => {
    const sorted = [...list].sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      // Keep ordering stable when sortOrder matches.
      return (a.item.name ?? '').localeCompare(b.item.name ?? '');
    });
    const seen = new Set<string>();
    const out: MenuItem[] = [];
    for (const r of sorted) {
      const key = r.item.path ?? r.item.name;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r.item);
    }
    return out;
  };

  const injectIntoTree = (node: MenuItem): MenuItem => {
    const children = (node.children ?? []).map(injectIntoTree);
    if (!children.length) return { ...node, children };

    const next: MenuItem[] = [];
    for (const child of children) {
      const anchorCandidates: string[] = [];
      if (typeof child.path === 'string' && child.path.length) anchorCandidates.push(child.path);
      if (child.views?.length) {
        for (const v of child.views) {
          if (typeof v.path === 'string' && v.path.length) anchorCandidates.push(v.path);
        }
      }

      if (anchorCandidates.length) {
        const beforeRecords: InsertRecord[] = [];
        for (const p of anchorCandidates) {
          const list = insertionsBefore.get(p);
          if (list?.length) beforeRecords.push(...list);
        }
        if (beforeRecords.length) next.push(...sortInsertions(beforeRecords));
      }
      next.push(child);
      if (anchorCandidates.length) {
        const afterRecords: InsertRecord[] = [];
        for (const p of anchorCandidates) {
          const list = insertionsAfter.get(p);
          if (list?.length) afterRecords.push(...list);
        }
        if (afterRecords.length) next.push(...sortInsertions(afterRecords));
      }
    }

    return { ...node, children: next };
  };

  return withCatalog.map(injectIntoTree);
}

/** Add shared e-form app pages configured for the E-Forms catalog. */
export function withEFormAppPagesInSidebar(
  items: MenuItem[],
  pages: Array<{ id: number; name: string; sidebarPlacements?: unknown }>,
): MenuItem[] {
  if (!pages.length) return items;
  const withCatalog = items.map((item) => {
    if (item.name !== 'E-Forms') return item;
    const existing = item.children ?? [];
    const withoutDynamic = existing.filter(
      (child) =>
        !(typeof child.path === 'string' && /^\/e-forms\/apps\/\d+(\?.*)?$/.test(child.path)),
    );
    const appItems: MenuItem[] = pages
      .filter((page) =>
        normalizeReportSidebarPlacements(page.sidebarPlacements).some(
          (placement) => placement.mode === 'catalog_only',
        ),
      )
      .map((page) => ({
        name: page.name,
        path: `/e-forms/apps/${page.id}`,
        moduleGroupKey: 'e_forms',
        permissionModuleKey: 'e_form_submissions',
        icon: ListIcon,
      }));
    return {
      ...item,
      defaultExpanded: appItems.length ? true : item.defaultExpanded,
      children: [...withoutDynamic, ...appItems],
    };
  });
  const appItem = (page: { id: number; name: string }): MenuItem => ({
    name: page.name,
    path: `/e-forms/apps/${page.id}?nav=shortcut`,
    moduleGroupKey: 'e_forms',
    permissionModuleKey: 'e_form_submissions',
    icon: ListIcon,
  });
  const inject = (nodes: MenuItem[]): MenuItem[] => {
    const output: MenuItem[] = [];
    for (const original of nodes) {
      const node = original.children?.length
        ? { ...original, children: inject(original.children) }
        : original;
      const anchors = [
        typeof node.path === 'string' ? node.path : '',
        ...(node.views ?? []).map((view) => view.path),
      ].filter(Boolean);
      const before: Array<{ page: (typeof pages)[number]; sortOrder: number }> = [];
      const after: Array<{ page: (typeof pages)[number]; sortOrder: number }> = [];
      for (const page of pages) {
        for (const placement of normalizeReportSidebarPlacements(page.sidebarPlacements)) {
          if (
            placement.mode !== 'shortcut' ||
            !anchors.includes(placement.anchorPath)
          ) {
            continue;
          }
          (placement.insert === 'before' ? before : after).push({
            page,
            sortOrder: placement.sortOrder ?? 0,
          });
        }
      }
      const sort = (
        rows: Array<{ page: (typeof pages)[number]; sortOrder: number }>,
      ) =>
        rows
          .sort(
            (a, b) =>
              a.sortOrder - b.sortOrder || a.page.name.localeCompare(b.page.name),
          )
          .filter(
            (row, index, all) =>
              all.findIndex((candidate) => candidate.page.id === row.page.id) === index,
          )
          .map((row) => appItem(row.page));
      output.push(...sort(before), node, ...sort(after));
    }
    return output;
  };
  return inject(withCatalog);
}

/** Inject saved e-form reports (catalog under E-Forms + module shortcuts) → view page. */
export function withSavedEFormReportsInSidebar(
  items: MenuItem[],
  reports: Array<{
    id: number;
    definitionId: number;
    name: string;
    sidebarPlacements?: unknown;
  }>,
): MenuItem[] {
  if (!reports.length) return items;

  const withCatalog = items.map((item) => {
    if (item.name !== 'E-Forms') return item;
    const existing = item.children ?? [];
    const withoutDynamic = existing.filter(
      (c) =>
        !(
          typeof c.path === 'string' &&
          /^\/e-forms\/\d+\/reports\/\d+(\?.*)?$/.test(c.path)
        ),
    );
    const reportItems: MenuItem[] = [];
    for (const r of reports) {
      const placements = normalizeReportSidebarPlacements(r.sidebarPlacements);
      if (!placements.some((p) => p.mode === 'catalog_only')) continue;
      reportItems.push({
        name: r.name,
        path: `/e-forms/${r.definitionId}/reports/${r.id}`,
        moduleGroupKey: 'e_forms',
        permissionModuleKey: 'e_form_submissions',
        icon: ListIcon,
      });
    }
    return {
      ...item,
      defaultExpanded: true,
      children: [...withoutDynamic, ...reportItems],
    };
  });

  type InsertRecord = {
    anchorPath: string;
    insert: 'before' | 'after';
    sortOrder: number;
    item: MenuItem;
  };

  const insertionsBefore = new Map<string, InsertRecord[]>();
  const insertionsAfter = new Map<string, InsertRecord[]>();

  for (const report of reports) {
    const placements = normalizeReportSidebarPlacements(report.sidebarPlacements);
    const reportItem: MenuItem = {
      name: report.name,
      path: `/e-forms/${report.definitionId}/reports/${report.id}?nav=shortcut`,
      moduleGroupKey: 'e_forms',
      permissionModuleKey: 'e_form_submissions',
      icon: ListIcon,
    };

    for (const p of placements) {
      if (p.mode !== 'shortcut') continue;
      const rec: InsertRecord = {
        anchorPath: p.anchorPath,
        insert: p.insert,
        sortOrder: p.sortOrder ?? 0,
        item: reportItem,
      };
      const list =
        p.insert === 'before'
          ? insertionsBefore.get(p.anchorPath) ?? []
          : insertionsAfter.get(p.anchorPath) ?? [];
      list.push(rec);
      if (p.insert === 'before') insertionsBefore.set(p.anchorPath, list);
      else insertionsAfter.set(p.anchorPath, list);
    }
  }

  if (!insertionsBefore.size && !insertionsAfter.size) {
    return withCatalog;
  }

  const sortInsertions = (list: InsertRecord[]): MenuItem[] => {
    const sorted = [...list].sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return (a.item.name ?? '').localeCompare(b.item.name ?? '');
    });
    const seen = new Set<string>();
    const out: MenuItem[] = [];
    for (const r of sorted) {
      const key = r.item.path ?? r.item.name;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r.item);
    }
    return out;
  };

  const injectIntoTree = (node: MenuItem): MenuItem => {
    const children = (node.children ?? []).map(injectIntoTree);
    if (!children.length) return { ...node, children };

    const next: MenuItem[] = [];
    for (const child of children) {
      const anchorCandidates: string[] = [];
      if (typeof child.path === 'string' && child.path.length) anchorCandidates.push(child.path);
      if (child.views?.length) {
        for (const v of child.views) {
          if (typeof v.path === 'string' && v.path.length) anchorCandidates.push(v.path);
        }
      }

      if (anchorCandidates.length) {
        const beforeRecords: InsertRecord[] = [];
        for (const p of anchorCandidates) {
          const list = insertionsBefore.get(p);
          if (list?.length) beforeRecords.push(...list);
        }
        if (beforeRecords.length) next.push(...sortInsertions(beforeRecords));
      }
      next.push(child);
      if (anchorCandidates.length) {
        const afterRecords: InsertRecord[] = [];
        for (const p of anchorCandidates) {
          const list = insertionsAfter.get(p);
          if (list?.length) afterRecords.push(...list);
        }
        if (afterRecords.length) next.push(...sortInsertions(afterRecords));
      }
    }

    return { ...node, children: next };
  };

  return withCatalog.map(injectIntoTree);
}

export function flattenSidebarItemsForSearch(
  items: MenuItem[],
  parentTrail: string[] = [],
): { label: string; path: string; group?: string; icon?: React.ReactNode }[] {
  const out: { label: string; path: string; group?: string; icon?: React.ReactNode }[] = [];
  for (const item of items) {
    if (item.sectionLabel) continue;
    if (item.name === 'Bookmarks') continue;

    const trail = item.path || item.views?.length ? [...parentTrail, item.name] : parentTrail;
    const breadcrumb = parentTrail.length > 0 ? parentTrail.join(' › ') : undefined;
    const path = item.path ?? item.views?.[0]?.path;

    if (path) {
      out.push({ label: item.name, path, group: breadcrumb, icon: item.icon });
    }

    if (item.children?.length) {
      const childTrail = [...parentTrail, item.name];
      for (const child of item.children) {
        const childPath = child.path ?? child.views?.[0]?.path;
        const childBreadcrumb = childTrail.join(' › ');
        if (childPath) {
          out.push({ label: child.name, path: childPath, group: childBreadcrumb, icon: child.icon });
        }
        if (child.children?.length) {
          out.push(...flattenSidebarItemsForSearch(child.children, childTrail));
        }
      }
    }
  }

  for (const report of flattenReportCatalogForSearch()) {
    out.push({ label: report.label, path: report.path, group: report.group });
  }
  for (const report of flattenCrewReportCatalogForSearch()) {
    out.push({ label: report.label, path: report.path, group: report.group });
  }

  return out;
}

/**
 * Collapse flat sidebar roots (with sectionLabel dividers) into the four top-menu
 * groups: General, Work, Reference Data, System.
 */
export function buildTopMenuRoots(items: MenuItem[]): MenuItem[] {
  const general: MenuItem[] = [];
  const work: MenuItem[] = [];
  let reference: MenuItem | null = null;
  let system: MenuItem | null = null;
  let bucket: 'general' | 'work' | 'reference' | 'system' = 'general';

  for (const item of items) {
    if (item.name === 'Bookmarks') continue;

    const section = (item.sectionLabel || '').trim().toLowerCase();
    if (section === 'general') {
      bucket = 'general';
      continue;
    }
    if (section === 'work') {
      bucket = 'work';
      continue;
    }
    if (section === 'reference' || section === 'reference data') {
      bucket = 'reference';
      continue;
    }
    if (section === 'system') {
      bucket = 'system';
      continue;
    }
    if (item.sectionLabel) continue;

    if (bucket === 'general') general.push(item);
    else if (bucket === 'work') work.push(item);
    else if (bucket === 'reference') {
      reference = {
        ...item,
        name: 'Reference Data',
        children: item.children?.length ? item.children : [item],
      };
    } else if (bucket === 'system') {
      system = item;
    }
  }

  const roots: MenuItem[] = [];
  if (general.length > 0) {
    roots.push({
      name: 'General',
      children: general,
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
    });
  }
  if (work.length > 0) {
    roots.push({
      name: 'Work',
      children: work,
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    });
  }
  if (reference) roots.push(reference);
  if (system) roots.push(system);
  return roots;
}
